import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@components/ui/Button';
import {
  AddCustomCard,
  AddSpecialtyModal,
  mapSpecialtiesError,
  SpecialtyServiceCard,
  useSpecialties,
  useSystemSpecialties,
} from '@features/specialties';
import type { CreateSpecialtyRequest, Specialty, SystemSpecialty } from '@features/specialties';
import { CloseIcon } from '@lib/icons';
import { cn } from '@/styles/tailwind-utils';

interface Props {
  onNext: () => void;
  onBack: () => void;
}

function ArrowRight() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowLeft() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d="M13 8H3M7 12l-4-4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-3 gap-3">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-28 animate-pulse rounded-md bg-muted" />
      ))}
    </div>
  );
}

export function Step3Services({ onNext, onBack }: Props) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const {
    specialties,
    isLoading: isLoadingAccount,
    fetchError: accountFetchError,
    create,
    isCreating,
    createError,
    creatingSpecialtyCode,
    update,
    updatingSpecialtyId,
    updateError,
  } = useSpecialties();

  const {
    systemSpecialties,
    isLoading: isLoadingSystem,
    fetchError: systemFetchError,
  } = useSystemSpecialties();

  const accountByCode = useMemo(() => {
    const map = new Map<string, Specialty>();
    specialties.forEach((s) => {
      if (s.specialtyCode) map.set(s.specialtyCode, s);
    });
    return map;
  }, [specialties]);

  const systemCodes = useMemo(
    () => new Set(systemSpecialties.map((s) => s.specialtyCode)),
    [systemSpecialties],
  );

  const customSpecialties = useMemo(
    () => specialties.filter((s) => !s.specialtyCode || !systemCodes.has(s.specialtyCode)),
    [specialties, systemCodes],
  );

  const isMutating = !!creatingSpecialtyCode || !!updatingSpecialtyId;

  function handleSystemToggle(sys: SystemSpecialty) {
    setToggleError(null);
    const existing = accountByCode.get(sys.specialtyCode);

    if (!existing) {
      create(
        { name: sys.name, specialtyCode: sys.specialtyCode, isActive: true, onlineBookingEnabled: true },
        {
          onError: (err) =>
            setToggleError(mapSpecialtiesError(err, t, 'specialties.errors.saveFailed')),
        },
      );
    } else if (!existing.isActive) {
      update(
        { specialtyId: existing.id, body: { isActive: true } },
        {
          onError: (err) =>
            setToggleError(mapSpecialtiesError(err, t, 'specialties.errors.saveFailed')),
        },
      );
    } else {
      update(
        { specialtyId: existing.id, body: { isActive: false } },
        {
          onError: (err) =>
            setToggleError(mapSpecialtiesError(err, t, 'specialties.errors.saveFailed')),
        },
      );
    }
  }

  function handleCustomToggle(s: Specialty) {
    setToggleError(null);
    update(
      { specialtyId: s.id, body: { isActive: !s.isActive } },
      {
        onError: (err) =>
          setToggleError(mapSpecialtiesError(err, t, 'specialties.errors.saveFailed')),
      },
    );
  }

  function handleCreate(data: CreateSpecialtyRequest) {
    create(data, { onSuccess: () => setModalOpen(false) });
  }

  const isPageLoading = isLoadingSystem || isLoadingAccount;
  const pageError = systemFetchError ?? accountFetchError;

  const isModalCreating = isCreating && creatingSpecialtyCode === null;
  const modalCreateError =
    createError && creatingSpecialtyCode === null
      ? mapSpecialtiesError(createError, t, 'specialties.errors.saveFailed')
      : updateError
        ? mapSpecialtiesError(updateError, t, 'specialties.errors.saveFailed')
        : null;

  return (
    <div>
      <h2 className="font-display text-[2rem] font-semibold leading-tight text-foreground">
        {t('onboarding.step3.title')}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">{t('onboarding.step3.subtitle')}</p>

      <div className="mt-8">
        {isPageLoading ? (
          <SkeletonGrid />
        ) : pageError ? (
          <div role="alert" className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
            {mapSpecialtiesError(pageError, t, 'specialties.errors.loadFailed')}
          </div>
        ) : (
          <>
            {toggleError && (
              <div
                role="alert"
                className="mb-4 flex items-start justify-between gap-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
              >
                <span>{toggleError}</span>
                <button
                  type="button"
                  aria-label={t('specialties.dismissError')}
                  onClick={() => setToggleError(null)}
                  className="shrink-0 text-destructive/60 hover:text-destructive"
                >
                  <CloseIcon />
                </button>
              </div>
            )}

            <div className={cn('grid grid-cols-3 gap-3', isMutating && 'pointer-events-none')}>
              {systemSpecialties.map((sys) => {
                const existing = accountByCode.get(sys.specialtyCode);
                const isActive = existing?.isActive === true;
                const isPending =
                  creatingSpecialtyCode === sys.specialtyCode ||
                  (!!existing && updatingSpecialtyId === existing.id);
                return (
                  <SpecialtyServiceCard
                    key={sys.id}
                    name={sys.name}
                    icon={sys.icon}
                    isActive={isActive}
                    isPending={isPending}
                    onToggle={() => handleSystemToggle(sys)}
                  />
                );
              })}
              <AddCustomCard onClick={() => setModalOpen(true)} />
            </div>

            {customSpecialties.length > 0 && (
              <div className="mt-8">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('specialties.customSection')}
                </h3>
                <div className={cn('grid grid-cols-3 gap-3', isMutating && 'pointer-events-none')}>
                  {customSpecialties.map((s) => (
                    <SpecialtyServiceCard
                      key={s.id}
                      name={s.name}
                      icon={s.icon ?? null}
                      isActive={s.isActive}
                      isPending={updatingSpecialtyId === s.id}
                      onToggle={() => handleCustomToggle(s)}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <Button type="button" variant="ghost" size="lg" leftIcon={<ArrowLeft />} onClick={onBack}>
          {t('onboarding.back')}
        </Button>
        <Button type="button" size="lg" rightIcon={<ArrowRight />} onClick={onNext}>
          {t('onboarding.continue')}
        </Button>
      </div>

      <AddSpecialtyModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleCreate}
        isSubmitting={isModalCreating}
        errorMessage={modalCreateError}
      />
    </div>
  );
}
